import React, { ReactNode, useEffect } from 'react';
import { SgwtHelpCenter, SgwtHelpCenterHTMLElement } from '@sgwt/sgwt-widgets-react';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'sg-error-page': {
        code: 404 | 500 | 503;
        theme?: 'dark' | 'standard';
        'action-button-label'?: string;
        'action-button-link'?: string;
        'contact-us'?: 'help-center' | string;
        fullscreen?: boolean;
        'description-label'?: string;
        'title-label'?: string;
      };
    }
  }
}

interface ErrorBoundaryState {
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

const LAZY_LOAD_ERROR_MESSAGE = 'Failed to fetch dynamically imported module';

// Push information of error in Matomo...
const trackEvent = (action: string, name: string) => {
  (window as any)._paq?.push(['trackEvent', 'ErrorBoundary', action, name]);
};

const AUTO_REFRESH_KEY = 'platformization.automatic-refresh';

/**
 * Function used to automatically refresh the page when the user encounters the "Failed to fetch dynamically imported module..."
 * error. We only refresh the page once to avoid an infinite loop.
 */
const automaticRefresh = () => {
  const hasAlreadyTriedToRefresh = sessionStorage.getItem(AUTO_REFRESH_KEY);
  if (hasAlreadyTriedToRefresh) {
    // the key in sessionStorage exists, meaning we already refresh the page...
    sessionStorage.removeItem(AUTO_REFRESH_KEY);
  } else {
    sessionStorage.setItem(AUTO_REFRESH_KEY, 'true');
    window.location.reload();
  }
};

/**
 * We handle specifically the "Failed to fetch dynamically imported module..." that can happen when the user continues
 * to use the application _during_ a release (details on https://stackoverflow.com/q/78637125/26457).
 */
const DynamicImportModuleError = ({ error }: { error: Error }) => {
  useEffect(() => {
    automaticRefresh();
  }, []);

  const refreshPage = () => {
    trackEvent('refresh-after-lazy-loading-error', '');
    setTimeout(() => window.location.reload(), 100); // Give sometime to track event.
  };

  const contactSupport = () => (document.querySelector('sgwt-help-center') as SgwtHelpCenterHTMLElement)?.form();

  return (
    <div style={{ height: '100vh' }}>
      <SgwtHelpCenter
        application-id="modernization"
        messageOnly
        messageTemplate={`Hello support.
            
I encountered an issue while using the service.
-----------------
Technical details
Error message: ${error.message}
Stack trace: ${error.stack}
URL: ${document.location.href}
            `}
      />

      <div className="container">
        <section className="text-center my-5">
          <i className="icon icon-xl text-warning">dangerous</i>
          <h1 className="my-3">An error occured</h1>
          <p className="mb-4 fs-16">
            Sorry! Something wrong happened while loading a ressource from Platformization.
            <br />
            It seems that your current application session is now outdated,{' '}
            <span className="fw-bold">a page refresh may solve that issue</span>.
            <br />
            If the problem persists, please contact our Support team.
          </p>

          <div>
            <button className="btn btn-lg btn-icon-start btn-outline-warning me-3" onClick={contactSupport}>
              <i className="icon icon-sm">support</i>
              Contact Support team
            </button>
            <button className="btn btn-lg btn-icon-start btn-outline-info" onClick={refreshPage}>
              <i className="icon icon-sm">refresh</i>
              Refresh the page
            </button>
          </div>
        </section>

        <div style={{ marginTop: 100 }}>
          <div className="alert alert-discreet-secondary">
            <div className="fw-bold mb-3">Technical details</div>
            <div>{error.message}</div>
            <hr />
            <code className="text-primary">{error.stack}</code>
          </div>
        </div>
      </div>
    </div>
  );
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  public componentDidCatch(errorDetected: Error, errorInfo: React.ErrorInfo) {
    console.error(
      `ErrorBoundary error[${errorDetected.message}] stack[${errorDetected.stack}] errorInfo[${errorInfo}]`
    );
  }

  public render() {
    const { error } = this.state;
    const { children } = this.props;

    if (error) {
      // Handle specific error related to Lazy Loading issue.
      if (error.message.startsWith(LAZY_LOAD_ERROR_MESSAGE)) {
        trackEvent('lazy-loading', error.message);
        return <DynamicImportModuleError error={error} />;
      }
      trackEvent('error', error.message);
      return (
        <div style={{ height: '100vh' }}>
          <SgwtHelpCenter
            application-id="modernization"
            messageOnly
            messageTemplate={`Hello support.
            
I encountered an issue while using the service.
-----------------
Technical details
Error message: ${error.message}
Stack trace: ${error.stack}
            `}
          />

          <sg-error-page
            code={500}
            action-button-label="Back to home"
            action-button-link="/"
            contact-us="help-center"
            fullscreen={true}
            description-label={error.message}
            title-label="Platformization-Error"
          />
        </div>
      );
    }

    return children;
  }
}
